import {
  ALL_CATEGORY_VALUE,
  CategoryCountsMap,
  CategoryVariantPacksMap,
  getCategoriesForVariantPack,
  NULL_CATEGORY_VALUE,
} from '@/utils/category';
import { CatalogCategoryType } from '@/contexts/item-context-types';
import {
  ItemDisplayOptions,
  useItemDisplayOptions,
} from '@/contexts/new-order-context';
import { SearchCatalog_VariantPackFieldsFragment } from '@/types';
import { useDynamicConfigValue } from './use-dynamic-config-value';
import { useMemo } from 'react';
import { useVariantPackInfoContext } from '@/contexts/variant-pack-info-context';

/**
 * Groups variant packs based on item display options and returns the number of groups
 * @param variantPacks - list of variant packs to count
 * @param itemDisplayOptions - item display options to use to group the variant packs
 * @returns the number of grouped variant packs
 */
const getGroupedVariantPacksCount = (
  variantPacks: SearchCatalog_VariantPackFieldsFragment[],
  itemDisplayOptions: ItemDisplayOptions,
) => {
  const groupedVariantPackKeys: Set<string> = new Set();
  for (const variantPack of variantPacks) {
    const groupKey = itemDisplayOptions.itemModifiersEnabled
      ? variantPack.item.uuid
      : itemDisplayOptions.displayItemCode
        ? variantPack.external_item_id
        : null;

    if (groupKey) {
      groupedVariantPackKeys.add(groupKey);
    } else {
      groupedVariantPackKeys.add(variantPack.uuid);
    }
  }

  return groupedVariantPackKeys.size;
};

interface Params {
  categories: CatalogCategoryType[];
  searchResults: SearchCatalog_VariantPackFieldsFragment[];
}

export const useCategoryCountsMap = ({ categories, searchResults }: Params) => {
  const itemDisplayOptions = useItemDisplayOptions();
  const itemGroupsEnabled = useDynamicConfigValue('item_groups_enabled');
  const { childVariantPackGroupMap } = useVariantPackInfoContext();

  return useMemo<CategoryCountsMap>(() => {
    if (!searchResults.length) {
      return new Map();
    }

    const categoryVariantPacksMap: CategoryVariantPacksMap = new Map();
    const searchResultsMap: {
      [variantPackUUID: string]: SearchCatalog_VariantPackFieldsFragment;
    } = {};
    for (const searchResult of searchResults) {
      const variantPackCategories = getCategoriesForVariantPack(
        searchResult,
        Boolean(itemGroupsEnabled),
      );

      for (const category of variantPackCategories) {
        const categoryVariantPacks =
          categoryVariantPacksMap.get(category) ?? new Set();
        categoryVariantPacksMap.set(
          category,
          categoryVariantPacks.add(searchResult.uuid),
        );
      }

      searchResultsMap[searchResult.uuid] = searchResult;
    }

    // Create a list of lists, where each sublist consists of a category key
    // and its subcategories
    const categoryKeysList = categories.reduce(
      (result, category) => {
        if (itemGroupsEnabled) {
          if (category.uuid) {
            result.push([
              category.uuid,
              ...(childVariantPackGroupMap[category.uuid] ?? []),
            ]);
          }
        } else {
          result.push([category.name ?? NULL_CATEGORY_VALUE]);
        }

        return result;
      },
      [[ALL_CATEGORY_VALUE]],
    );

    // Iterate through categories, get unique variant packs for each category
    // and its subcategories, and count the number of grouped variant packs
    const categoryCountsMap: CategoryCountsMap = new Map();
    for (const categoryKeys of categoryKeysList) {
      const variantPacksMap = new Map<
        string,
        SearchCatalog_VariantPackFieldsFragment
      >();

      for (const categoryKey of categoryKeys) {
        const categoryVariantPackUUIDs =
          categoryVariantPacksMap.get(categoryKey) || new Set();
        for (const variantPackUUID of categoryVariantPackUUIDs) {
          const variantPack = searchResultsMap[variantPackUUID];
          if (variantPack) {
            variantPacksMap.set(variantPack.uuid, variantPack);
          }
        }
      }

      const variantPacks = Array.from(variantPacksMap.values());
      categoryCountsMap.set(
        categoryKeys[0],
        getGroupedVariantPacksCount(variantPacks, itemDisplayOptions),
      );
    }

    return categoryCountsMap;
  }, [
    categories,
    childVariantPackGroupMap,
    itemDisplayOptions,
    itemGroupsEnabled,
    searchResults,
  ]);
};
